import { HttpErrorResponse } from "@angular/common/http";
import { inject, WritableSignal, signal, computed, Injectable, ResourceRef, Signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { toSignal } from "@angular/core/rxjs-interop";
import { of } from "rxjs";
import { EstateDataService } from "../estate-data.service";
import { ImagePackage } from "src/models/common/image.model";
import { ResourceService } from "../resources/resource.service";
import { IUnitAggregatedSpecialConstraintData } from "src/models/units/special-constraints.model";
import { ExternalLinksService } from "../externallinks.service";
import { ICadastreLandRegistrationReportUrls } from "@models/public-info-urls";
import { EnergyDataService } from "../energy-data.service";
import { IForeclosureDiscount } from "src/app/infopage/components/key-info-physical-exposure/key-info-physical-exposure.component";
import { SalesDataService } from "../sales-data.service";
import { UnitDataService } from "../unit-data.service";
import { ICadastre } from "src/models/estate/key-info.model";
import { IValuationHistory } from "src/models/estate/valuation-history.model";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";

@Injectable({
    providedIn: "root",
})
export class FunctionalUnitDataService {
    private readonly authService = inject(EvAuthorizationService);
    private readonly externalLinksService = inject(ExternalLinksService);
    private readonly salesDataService = inject(SalesDataService);
    private readonly estateDataService = inject(EstateDataService);
    private readonly energyDataService = inject(EnergyDataService);
    private readonly resourceService = inject(ResourceService);
    private readonly unitDataService = inject(UnitDataService);

    private unitId: WritableSignal<number | null> = signal(null);
    private fetchEnergyUnitInfo: WritableSignal<boolean> = signal(false);
    private fetchNearbySales: WritableSignal<boolean> = signal(false);
    private fetchNearbySiteSales: WritableSignal<boolean> = signal(false);
    private fetchPublicAddressInfo: WritableSignal<boolean> = signal(false);
    private fetchPropertyInformation: WritableSignal<boolean> = signal(false);

    private energyUnitInfoTrigger = computed(() => { return this.fetchEnergyUnitInfo() ? this.unitId() : null; });
    private nearbySalesTrigger = computed(() => { return this.fetchNearbySales() ? this.unitId() : null; });
    private nearbySiteSalesTrigger = computed(() => { return this.fetchNearbySiteSales() ? this.unitId() : null; });
    private publicAddressInfoTrigger = computed(() => { return this.fetchPublicAddressInfo() ? this.unitId() : null; });
    private buildingsTrigger = computed(() => { return this.fetchPropertyInformation() ? this.unitId() : null; });
    private hasPhysicalExposuresRole = toSignal(this.authService.hasPermission(EVServiceType.EvWeb_PhysicalExposure));
    private hasSpecialConstraintsRole = toSignal(this.authService.hasPermission(EVServiceType.EvWeb_SpecialConstraints));
    
    aggregatedSpecialConstraints = computed(() => {
        if (this.specialConstraintsResource.hasValue()) {
            return this.specialConstraintsResource.value();
        }
        return null;
    });

    cadastre = computed(() => {
        const keyInfo = this.functionalUnitInfo();
        if (keyInfo?.parentCadastre) {
            return keyInfo.parentCadastre;
        }
        return null;
    });

    foreclosureDiscount: Signal<IForeclosureDiscount> = computed(() => {
        if (this.foreclosureDiscountResource.hasValue()) {
            return this.foreclosureDiscountResource.value();
        }
        return null;
    });
    
    functionalUnitInfo = computed(() => {
        if (this.functionalUnitInfoResource.hasValue()) {
            return this.functionalUnitInfoResource.value();
        }
        return null;
    });

    functionalUnitInfoErrorMessage = computed(() => {
        const error = this.functionalUnitInfoResource.error();
        if (error) {
            // HttpDataService.getResource pakker HttpErrorResponse inn i en Error med responsen som cause.
            const httpError = (error.cause ?? error) as HttpErrorResponse;
            if (httpError?.status === 404) {
                return "Fant ingen nøkkelinfo for boenheten.";
            }
            return `Klarte ikke å laste inn nøkkelinfo for boenheten.`;
        }
        return null;
    });

    imagePackages: Signal<ImagePackage[]> = computed(() => {
        let imagePackages: ImagePackage[] = [];
        if (this.blomImagesResource.hasValue()) {
            imagePackages = [...imagePackages, ...this.blomImagesResource.value()];
        }
        return imagePackages;
    });

    neighbourhoodData = computed(() => {
        if (this.neighbourhoodResource.hasValue()) {
            return this.neighbourhoodResource.value();
        }
        return null;
    });

    organizationNumber = computed(() => {
        const ornNr = this.functionalUnitInfo();
        return ornNr?.organizationNumber;
    });
    
    physicalExposures = computed(() => {
            if (this.physicalRiskResource.hasValue()) {
                return this.physicalRiskResource.value();
            }
        return null;
    });

    position = computed(() => {
        const keyInfo = this.functionalUnitInfo();
        if (keyInfo?.latitude && keyInfo?.longitude) {
            return {
                lat: keyInfo.latitude,
                lng: keyInfo.longitude,
            };
        }
        return null;
    });

    reportUrls: Signal<ICadastreLandRegistrationReportUrls | null> = computed(() => {
        const cadastre = this.cadastre();
        if (cadastre) {
            return this.createLandRegistrationUrls(cadastre);
        }
        return null;
    });

    setUnitId(unitId: number): void {
        this.unitId.set(unitId);
    }

    triggerLoadNearbySales(): void {
        this.fetchNearbySales.set(true);
    }

    triggerLoadNearbySiteSales(): void {
        this.fetchNearbySiteSales.set(true);
    }

    triggerLoadEnergyUnitInfo(): void {
        this.fetchEnergyUnitInfo.set(true);
    }

    triggerLoadPublicAddressInfo(): void {
        this.fetchPublicAddressInfo.set(true);
    }

    triggerLoadPropertyInformation(): void {
        this.fetchPropertyInformation.set(true);
    }

    businessManagerResource = this.estateDataService.getBusinessManagerResource(this.organizationNumber);
    energyUnitInfoResource = this.energyDataService.getUnitEnergyInfoResource(this.energyUnitInfoTrigger);
    foreclosureDiscountResource = this.estateDataService.getForeclosureDiscountResource(this.unitId);
    functionalUnitInfoResource = this.estateDataService.getFunctionalUnitKeyInfoResource(this.unitId);
    neighbourhoodResource = this.unitDataService.getNeighborhoodInfoResource(this.unitId);
    nearbySalesResource = this.salesDataService.getNearbySalesResource(this.nearbySalesTrigger);
    nearbySiteSalesResource = this.salesDataService.getNearbySiteSalesResource(this.nearbySiteSalesTrigger);
    physicalRiskResource = this.estateDataService.getPhysicalRiskResource(this.unitId, this.hasPhysicalExposuresRole);
    propertyInformationResource = this.estateDataService.getPropertyInformationResource(this.buildingsTrigger);
    publicAddressInfoResource = this.unitDataService.getPublicAddressInfoResource(this.publicAddressInfoTrigger);
    specialConstraintsResource: ResourceRef<IUnitAggregatedSpecialConstraintData> = this.estateDataService.getSpecialConstraintsResource(this.cadastre, this.hasSpecialConstraintsRole);
    valueHistoryResource: ResourceRef<IValuationHistory[]> = this.estateDataService.getValuationHistoryResource(this.unitId);

    private readonly blomImagesResource: ResourceRef<ImagePackage[]> = rxResource({
        params: () => this.position(),
        stream: ({ params: position }) => {
            if (position) {
                return this.resourceService.getBlomImagePackage({ lat: position.lat, lng: position.lng });
            }
            return of([]);
        },
    });

    private createLandRegistrationUrls(cadastre: ICadastre): ICadastreLandRegistrationReportUrls {
        let urls = {
            landRegistrationReportUrl: this.externalLinksService.getLandRegistrationReportUrl(
                cadastre.kNr,
                cadastre.gNr,
                cadastre.bNr,
                cadastre.fNr,
                cadastre.sNr,
                null,
                null,
                false
            ),
            landRegistrationReportUrlHistorical: this.externalLinksService.getLandRegistrationReportUrl(
                cadastre.kNr,
                cadastre.gNr,
                cadastre.bNr,
                cadastre.fNr,
                cadastre.sNr,
                null,
                null,
                true
            ),
            cadastreReportUrl: this.externalLinksService.getCadastreReportUrl(cadastre.kNr, cadastre.gNr, cadastre.bNr, cadastre.fNr, cadastre.sNr),
        };

        return urls;
    }
}





